import { isDefined } from "@clipboard-health/util-ts";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { type LatenessConfigResponse } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { ClipboardScoreActions } from "@src/appV2/redesign/ClipboardScore/utils/constants";
import {
  formatDurationLabel,
  formatDurationRangeLabel,
} from "@src/appV2/redesign/utils/formatDurationLabel";

import { type ScoreChange } from "../types";

const WORK_SHIFT_ACTIONS = new Set([
  ClipboardScoreActions.WORK_SHIFT,
  ClipboardScoreActions.CLOCKIN_TIMELINESS_BONUS,
  ClipboardScoreActions.START_SHIFT_LATE,
]);

interface BuildScoreChangesParams {
  scoreLogs: ClipboardScoreLog[];
  latenessMinutes: number;
  latenessConfig?: LatenessConfigResponse;
  /**
   * When the worker has a lateness credit, this is the full penalty from the
   * policy config (before the credit reduces it). When provided, the
   * START_SHIFT_LATE line item displays this value instead of the actual
   * clipboardScoreAdjustment.
   */
  fullLatenessPenalty?: number;
}

/**
 * Transforms raw clipboard score logs into ScoreChange[] for the EC clock-out flow.
 * Filters for work-shift-related actions and maps each to a display-ready score change.
 */
export function buildScoreChanges(params: BuildScoreChangesParams): ScoreChange[] {
  const { scoreLogs, latenessMinutes, latenessConfig, fullLatenessPenalty } = params;

  return scoreLogs
    .filter((log) => WORK_SHIFT_ACTIONS.has(log.action))
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .map((log) =>
      getScoreChangeForAction(log, { latenessMinutes, latenessConfig, fullLatenessPenalty })
    )
    .filter(isDefined);
}

function getLatenessLabel(
  latenessMinutes: number,
  latenessConfig?: LatenessConfigResponse
): string {
  const minutesLateString = formatDurationLabel(latenessMinutes, "minute", {
    keepOriginalUnit: true,
  });

  if (!isDefined(latenessConfig) || latenessConfig.length === 0) {
    return `${minutesLateString} late`;
  }

  const sortedRanges = latenessConfig
    .map((config) => config.minutesLateRange)
    .toSorted((a, b) => a.startFrom - b.startFrom);

  const matchedRange = sortedRanges.findLast((range) => latenessMinutes >= range.startFrom);

  if (!isDefined(matchedRange)) {
    return `${minutesLateString} late`;
  }

  if (isDefined(matchedRange.stopAt)) {
    return `${matchedRange.startFrom}-${matchedRange.stopAt} minutes late`;
  }

  return `${formatDurationRangeLabel(matchedRange.startFrom, undefined, "minute", {
    keepOriginalUnit: true,
  })} late`;
}

interface LatenessContext {
  latenessMinutes: number;
  latenessConfig?: LatenessConfigResponse;
  fullLatenessPenalty?: number;
}

function getScoreChangeForAction(
  log: ClipboardScoreLog,
  latenessContext: LatenessContext
): ScoreChange | undefined {
  const adjustment = log.clipboardScoreAdjustment ?? 0;

  switch (log.action) {
    case ClipboardScoreActions.WORK_SHIFT: {
      return { label: "Completed shift", value: adjustment, color: "positive" };
    }
    case ClipboardScoreActions.CLOCKIN_TIMELINESS_BONUS: {
      return { label: "On-time bonus", value: adjustment, color: "positive" };
    }
    case ClipboardScoreActions.START_SHIFT_LATE: {
      const { latenessMinutes, latenessConfig, fullLatenessPenalty } = latenessContext;
      return {
        label: getLatenessLabel(latenessMinutes, latenessConfig),
        value: isDefined(fullLatenessPenalty) ? fullLatenessPenalty : adjustment,
        color: "negative",
      };
    }
    default: {
      return undefined;
    }
  }
}
